import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Check } from 'lucide-react';

const CheckboxElement = ({
  element,
  isEditor = false,
  canInteract = true,
  onToggle,
  syncedChecked = null
}) => {
  const { content = {}, style = {} } = element;
  const boxColor = content.boxColor || '#ffffff';
  const borderColor = content.borderColor || '#3d6b3d';
  const checkColor = content.checkColor || '#3d6b3d';
  const borderWidth = content.borderWidth ?? 3;

  const [isChecked, setIsChecked] = useState(content.isChecked || false);

  useEffect(() => {
    if (syncedChecked !== null && syncedChecked !== undefined) {
      setIsChecked(Boolean(syncedChecked));
    }
  }, [syncedChecked]);

  const handleToggle = (e) => {
    if (isEditor || !canInteract) return;
    e.stopPropagation();
    const next = !isChecked;
    setIsChecked(next);
    if (onToggle) onToggle(next);
  };

  const showCheck = isEditor ? Boolean(content.isChecked) : isChecked;

  // The wrapper's on-screen aspect depends on the author's width%/height%
  // over a 16:9 slide, and rounding at very small pixel sizes can leave the
  // container off-square. Measure the wrapper and size the visible box to the
  // smaller dimension in pixels — this guarantees a true square regardless of
  // the container's actual rendered size (thumbnail, small resize, zoom).
  const wrapperRef = useRef(null);
  const [boxSize, setBoxSize] = useState(0);
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const size = Math.max(0, Math.min(rect.width, rect.height));
      setBoxSize(size);
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Scale the border so a shrunken checkbox doesn't collapse into a slab of
  // stroke. Cap at the author's `borderWidth` so it never gets thicker than
  // intended, and floor at 1px so the outline never disappears.
  const scaledBorderWidth = boxSize > 0
    ? Math.max(1, Math.min(borderWidth, Math.round(boxSize * 0.08)))
    : borderWidth;

  return (
    <div
      ref={wrapperRef}
      onClick={handleToggle}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: canInteract && !isEditor ? 'pointer' : 'default'
      }}
    >
      <div
        className="flex items-center justify-center rounded-md transition-colors"
        style={{
          width: `${boxSize}px`,
          height: `${boxSize}px`,
          backgroundColor: boxColor,
          border: `${scaledBorderWidth}px solid ${borderColor}`,
          borderRadius: style?.borderRadius ?? 8,
          boxSizing: 'border-box',
          flexShrink: 0
        }}
      >
        {showCheck && (
          <Check
            strokeWidth={4}
            style={{
              color: checkColor,
              width: '70%',
              height: '70%'
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CheckboxElement;
